import { tv } from "@ui/lib/tv";
import type { ReactNode } from "react";

const buttonDock = tv({
  slots: {
    wrapper: "@container w-full",
    root: "flex w-full gap-3",
    tertiarySection: "flex",
    actionSection: "flex gap-3",
  },
  variants: {
    align: {
      stretch: {
        root: "@2xl:flex-row flex-col @2xl:items-center @2xl:justify-between",
        tertiarySection: "@2xl:order-first order-last @2xl:[&>*]:w-auto [&>*]:w-full",
        actionSection: "@2xl:ms-auto @2xl:flex-row flex-col @2xl:items-center @2xl:gap-5",
      },
      end: {
        root: "flex-row items-center justify-end",
        actionSection: "ms-auto flex-row items-center",
      },
    },
  },
  defaultVariants: {
    align: "stretch",
  },
});

export interface ButtonDockProps {
  /** Primary CTA — rightmost when wide, near thumb when narrow */
  primaryAction: ReactNode;
  /** Secondary action (e.g., "Save and finish later") — right side when wide, above primary when narrow */
  secondaryAction?: ReactNode;
  /** Tertiary action (e.g., "Back") — left when wide, bottom when narrow */
  tertiaryAction?: ReactNode;
  /**
   * "stretch" (default): full-width stacked buttons until the container passes
   * 672px. "end": a right-aligned auto-width row at every width — for compact
   * dialogs, which never reach that breakpoint and would otherwise always stack.
   */
  align?: "stretch" | "end";
  className?: string;
}

/**
 * Container-query-driven action-button layout for page footers and modal footers.
 * Use inside a Dock for fixed-position pages, or inside a Dialog.Footer for modals.
 */
export function ButtonDock({
  primaryAction,
  secondaryAction,
  tertiaryAction,
  align = "stretch",
  className,
}: ButtonDockProps) {
  const styles = buttonDock({ align });

  return (
    <div className={styles.wrapper()}>
      <div role="toolbar" className={styles.root({ className })}>
        {tertiaryAction && <div className={styles.tertiarySection()}>{tertiaryAction}</div>}
        <div className={styles.actionSection()}>
          {secondaryAction}
          {primaryAction}
        </div>
      </div>
    </div>
  );
}
